import { useMemo, useEffect, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';
import { Stack, usePathname } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { Theme, TamaguiProvider } from 'tamagui';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import Constants from 'expo-constants';
import { idbPersisterStorage } from '../lib/idb-persister-storage';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import config from '@kiips/ui/tamagui.config';
import { trpc, createTRPCClient } from '../lib/trpc';
import { ThemeProvider, useTheme } from '../contexts/ThemeContext';
import { AuthProvider } from '../contexts/AuthContext';
import { UnifiedSidebarProvider } from '../contexts/UnifiedSidebarContext';
import { SidebarContentProvider } from '../contexts/SidebarContentContext';
import { MenuProvider } from '../contexts/MenuContext';
import { UpgradeSheetProvider } from '../contexts/UpgradeSheetContext';
import { GlobalSidebarHost } from '../components/GlobalSidebarHost';
import { SyncManagerInit } from '../components/SyncManagerInit';
import { ThemePrefSync } from '../components/ThemePrefSync';
import { PaymentStatusSync } from '../components/PaymentStatusSync';
import { SplashGate } from '../components/SplashGate';
import { ConsentSheet } from '../components/consent/ConsentSheet';
import { Seo, type SeoProps } from '../components/Seo';
import { SEO_BY_PATH } from '../lib/seo';
import { useThemeColors } from '../hooks/useThemeColors';
import { useItineraryStore } from '../store/itinerary-store';
import { AppToastProvider, ConfirmProvider, ErrorBoundary, HapticsProvider } from '@kiips/ui';
import {
  initObservability,
  reportError,
} from '../lib/observability';
import {
  initAnalytics,
  screen as trackScreen,
  optOutAnalytics,
} from '../lib/analytics';
import { installConsentBridge } from '../lib/consent-bridge';
import { interFontSources } from '../lib/fonts';

void SplashScreen.preventAutoHideAsync();
initObservability();
void initAnalytics();

// Boot opt-out (ADR-0065). Analytics defaults to opted-out until the user
// makes a decision: `optOutAnalytics` flips the adapter's gate AND calls
// the SDK's native optOut once `init` resolves. Error reporting shares the
// same PostHog client (ADR-0065), so this one call also suppresses
// exception capture until the user consents.
optOutAnalytics();

// Consent → SDK bridge. Mirrors the user's decision into per-provider
// optIn/optOut calls. Extracted to `lib/consent-bridge.ts` so it can be
// unit-tested without the Expo Router + Tamagui import surface.
installConsentBridge();

const persister = createAsyncStoragePersister({
  storage: idbPersisterStorage,
  // v3: web persistence moves to IndexedDB via idb-keyval (~5MB localStorage
  // quota lifted, writes are async). The key bump means the persister won't
  // load a v2 payload from localStorage; the orphaned entry is left for the
  // browser to GC.
  key: 'REACT_QUERY_CACHE_v3',
});


/**
 * Fires `analytics.screen(path)` on every Expo Router pathname change so the
 * funnel / retention dashboards work without per-screen instrumentation. The
 * raw pathname is what we ship (e.g. `/trip/[id]`) — PostHog can group by
 * route pattern. Auth-gated screens are still seen here pre-redirect, which
 * is acceptable noise; the dashboard can filter.
 */
function ScreenTracker() {
  const pathname = usePathname();
  useEffect(() => {
    if (pathname) trackScreen(pathname);
  }, [pathname]);
  return null;
}

/**
 * Drives the document head from the active route via the SEO_BY_PATH table, so
 * screens don't each hand-carry (and forget to update) a <Seo> block. Mounted
 * beside ScreenTracker — inside the navigation tree where usePathname resolves
 * per route at static-render time; the <Head> it emits portals out regardless
 * of tree position. Unlisted paths fall back to the default title/description
 * with no canonical, matching how dynamic/app routes behaved before.
 */
function RouteSeo() {
  const pathname = usePathname();
  const meta = SEO_BY_PATH[pathname];
  // Spread only the keys that exist (a listed path also gets its canonical
  // URL); unlisted paths render with the component's defaults. Building the
  // object avoids passing explicit `undefined` props under
  // exactOptionalPropertyTypes.
  const props: SeoProps = meta ? { path: pathname, ...meta } : {};
  return <Seo {...props} />;
}

// Mirrors the OS "Reduce Motion" flag (iOS Accessibility → Motion, Android
// developer/animation scale, web prefers-reduced-motion via RN-web) so
// HapticsProvider can mute haptics for users who opted out at the system
// level. ADR-0079 leaves the user-preference OR to the call site; this
// covers the system half until a Settings toggle ships.
function useReduceMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled()
      .then((value) => {
        if (mounted) setReduce(value);
      })
      .catch(() => undefined);
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduce);
    return () => {
      mounted = false;
      sub.remove();
    };
  }, []);
  return reduce;
}

// Rendered inside TamaguiProvider + Theme so useThemeColors() can call useThemeName() safely.
function NavigationStack({ queryClient, trpcClient }: { queryClient: QueryClient; trpcClient: ReturnType<typeof createTRPCClient> }) {
  const theme = useThemeColors();
  const reduceMotion = useReduceMotion();
  // User pref (Settings → Haptics) AND-ed with the system Reduce
  // Motion flag — either source can suppress in-app haptics.
  const hapticsPref = useItineraryStore((s) => s.hapticsEnabled);
  const hapticsEnabled = hapticsPref && !reduceMotion;
  return (
    <AuthProvider>
      <SplashGate>
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: 1000 * 60 * 60 * 24, // 24h
              buster: Constants.expoConfig?.version ?? '1.0.0',
            }}
          >
          <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <SyncManagerInit />
            <ThemePrefSync />
            <PaymentStatusSync />
            <HapticsProvider enabled={hapticsEnabled}>
            <AppToastProvider>
            <ConfirmProvider>
            <UnifiedSidebarProvider>
              <SidebarContentProvider>
                <MenuProvider>
                  <UpgradeSheetProvider>
                    <ScreenTracker />
                    <RouteSeo />
                    <Stack
                      screenOptions={{
                        headerShown: false,
                        contentStyle: { backgroundColor: theme.backgroundPage },
                      }}
                    />
                    <GlobalSidebarHost />
                    <ConsentSheet />
                  </UpgradeSheetProvider>
                </MenuProvider>
              </SidebarContentProvider>
            </UnifiedSidebarProvider>
            </ConfirmProvider>
            </AppToastProvider>
            </HapticsProvider>
          </trpc.Provider>
        </PersistQueryClientProvider>
      </SplashGate>
    </AuthProvider>
  );
}

function LayoutContent() {
  // Font sources are platform-split (see src/lib/fonts.ts): web serves
  // ~18 KB WOFF2 weights, native bundles the OTF it can actually load.
  // Both use `display: swap` so web shows fallback text immediately and
  // swaps Inter in on load instead of the default FOIT block.
  const [fontsLoaded, fontError] = useFonts(interFontSources);

  // Note: splash-hide is owned by `SplashGate`, which holds the splash
  // open until auth and store hydration have settled. We still need the
  // `if (!fontsLoaded && !fontError) return null` short-circuit below so
  // Tamagui doesn't mount unstyled — SplashGate then takes over once
  // children mount.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    // Disable double-tap-to-zoom on iOS Safari + suppress 300ms tap delay.
    // Viewport + apple-mobile-web-app meta tags live in `app/+html.tsx`.
    document.body.style.touchAction = 'pan-x pan-y';
  }, []);

  const { resolvedTheme, hydrated } = useTheme();

  const [queryClient, trpcClient] = useMemo(() => {
    const qc = new QueryClient({
      defaultOptions: {
        queries: { staleTime: 1000 * 60 * 5 }, // 5 minutes
      },
    });
    const tc = createTRPCClient();
    return [qc, tc];
  }, []);

  // Hold the (native splash-covered) first paint until fonts AND the persisted
  // theme mode have loaded. Mounting before `hydrated` would paint at the OS
  // scheme and then flip to the saved mode, stranding mount-captured styles
  // (native-stack background, portaled overlays) in the wrong theme until a
  // manual toggle. Native-only: on web `output: static` prerenders with no
  // effects, so gating on `hydrated` there would blank the prerendered HTML;
  // web swaps theme cleanly via Tamagui's CSS classes on client hydration.
  if ((!fontsLoaded && !fontError) || (Platform.OS !== 'web' && !hydrated)) {
    return null;
  }

  return (
    <TamaguiProvider config={config} defaultTheme={resolvedTheme}>
      <Theme name={resolvedTheme}>
        <StatusBar style={resolvedTheme === 'dark' ? 'light' : 'dark'} />
        <NavigationStack queryClient={queryClient} trpcClient={trpcClient} />
      </Theme>
    </TamaguiProvider>
  );
}

export default function RootLayout() {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <ErrorBoundary onError={(err) => reportError(err, { stage: 'render' })}>
        <ThemeProvider>
          <LayoutContent />
        </ThemeProvider>
      </ErrorBoundary>
    </GestureHandlerRootView>
  );
}
